import performanceMetaUtility from '@sparta.global.utilities/performance-meta-utility/dist/es6.js';
import spartaSingletonUtility from '@sparta.global.utilities/sparta-singleton-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import spartaFootnoteUtility from '@sparta.global.utilities/sparta-footnote-utility/dist/es6.js';
import spartaUILayersUtility from '@sparta.global.utilities/sparta-ui-layers-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';
import $ from '@sparta.global.vendors/jquery';
import { htmlEscaper } from '@sparta.credit-cards.helpers/credit-cards/dist/es6.js';

/**
|--------------------------------------------------
| Override data file for use with pcmOverrideEnabled.
| Will merge information in this file over PCM response information.
|
| From credit-card-utility.js:
| // If PCM Override enabled, merge data from override file
| if (siteParams.pcmOverrideEnabled) {
|   resolve({ ...response, ...overrideProductPropertiesMapData });
| }
|
|--------------------------------------------------
*/

const overrideProductPropertiesData = {
  /**
   * Use the following syntax:
   * locale: {
   *  pageID: {
   *    PCM_KEY: "Overridden Value"
   *  }
   * }
   *
   * Example:
   * en_US: {
      'products-bankamericard': {
        MARKETING_PRODUCT_NAME_LONG: 'BankAmericard<sup>®</sup> credit card',
      },
    }
   */
  en_US: {},
  es_US: {}
};
const overrideProductPropertiesMapData = {
  /**
   * Use the following syntax:
   * 'campaignID~pocode~locale': {
   *  PCM_KEY: "Overridden Value"
   * }
   *
   * Example:
   * '44054316~Y5~en_US': {
   *  MARKETING_PRODUCT_NAME_LONG: 'BankAmericard<sup>®</sup> credit card',
   * },
   */
};

/* eslint-disable import/no-unresolved */

/* global spaParams */

/**
 * @name        Credit Card Utility
 * @file        credit-card-utility.js
 * @description Utility for use on credit-cards product pages
 * @classdesc   Utility for use on credit-cards product pages
 * @global      Creates a global CreditCardUtility object
 * @author      removed
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 */

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */

const EVENTS = {
  // Show error with homepage link
  errorShowHome: 'errorShowHome',
  // Show error with credit card overview link
  errorShowGeneric: 'errorShowGeneric',
  // Show loader
  loaderShow: 'loaderShow',
  // Hide loader
  loaderHide: 'loaderHide',
  // Card list ajax and render complete
  cardListReady: 'cardListReady',
  // Card list filtering
  cardListFilter: 'cardListFilter',
  // Card list count
  cardListCount: 'cardListCount',
  // For add and remove compare
  compareUpdate: 'compareUpdate',
  // Publish compare data
  compareData: 'compareData',
  // For add compare table ready
  compareTableReady: 'compareTableReady',
  // For add compare table from modal
  compareTableUpdate: 'compareTableUpdate',
  // For clearing zip code cookie
  clearZipCodeCookie: 'clearZipCodeCookie',
  // Product details ready
  productDetailsReady: 'productDetailsReady',
  // Product details rendered
  productDetailsRendered: 'productDetailsRendered',
  // Quiz ready
  quizReady: 'quizReady',
  // Carousel ready
  carouselReady: 'carouselReady',
  // When card is removed
  cardRemoved: 'cardRemoved',
  // Add card layer opened
  addCardLayerOpen: 'addCardLayerOpen',
  valueChange: 'valueChange'
};

/**
 * Equivalent of _.isObject
 * @param {any} value The value to assert.
 * @returns {boolean} True if the given value is an object.
 */
const isObject = value => value && typeof value === 'object';

/**
 * Equivalent of _.map
 * @param {object} object The object ot map.
 * @param {function} callback The callback to invoke for each member of 'object'.
 * @returns {Array} An array containing the mapped values.
 */
const map = (object, callback) => {
  const results = [];
  Object.keys(object || {}).forEach(key => results.push(callback(object[key], key, object)));
  return results;
};

/**
 * Converts an object from key/value pairs to a query string. For example:
 * { foo: 'bar', baz: 'quxx' } would be translated to '?foo=bar&baz=quxx'.
 * @param {any} object The object to convert to a query string.
 * @returns {string} The formatted query string.
 * @export
 */
function stringify(object) {
  if (!isObject(object)) return '';
  const onValue = (value, key) => {
    const serialized = isObject(value) ? JSON.stringify(value) : value;
    return `${key}=${encodeURIComponent(serialized)}`;
  };
  return map(object, onValue).join('&');
}

/**
 * CreditCardUtility
 */
class CreditCardUtility {
  /**
   * constructor
   *
   * @description Only one instance of this utility is available since it's a singleton.
   */
  constructor() {
    this.Transport = spartaTransportUtility;
  }

  /**
   * @property {Object} EVENTS exposes event name constant
   * @description getter for EVENTS constant
   * @returns {Object} EVENTS object
   */
  get EVENTS() {
    // eslint-disable-line
    return EVENTS;
  }

  /**
   * Service request to obtain cards products.
   *
   * @param {Object} params object containing parameters to pass as a query string.
   * @param {Array} campaigns List of campaigns to retrieve properties
   * @param {Array} properties List of properties required for campaign
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  productPropertiesMap(params, campaigns = [], properties = []) {
    // Check if we have campaigns first - if not return promise
    // Since spanish jcr doesn't follow the same way en does
    // can eventually go back and make them common.
    if (!campaigns.length) {
      return new Promise(resolve => {
        // eslint-disable-next-line no-console
        console.warn('No campaigns requested.');
        resolve({});
      });
    }
    const {
      siteParams
    } = spaParams;
    const queryString = params ? `?${stringify(params)}` : '';
    const host = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}`;
    const url = `${host}/salesservices/productPropertiesMap${queryString}`;

    // Sparta 6.0.2-beta.11 is not populating the lang attribute correctly.
    // const locale = document.getElementsByTagName('html')[0].getAttribute('lang').replace('-', '_') || 'en_US';
    const locale = `${spaParams.language}_US`;
    const payload = JSON.stringify({
      locale,
      campaigns,
      properties
    });
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'POST',
        contentType: 'application/json',
        dataType: 'json',
        error: reject,
        data: payload,
        success: response => {
          // If PCM Override enabled, merge data from override file
          if (siteParams.pcmOverrideEnabled) {
            resolve({
              ...response,
              ...overrideProductPropertiesMapData
            });
          }
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Service request to obtain all properties for a given campaign
   * @param {Object} params - object containing the parameters to pass as a query string.
   * Needs to contain campaignID, poCode, and locale
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  productProperties(params) {
    const {
      siteParams,
      pageParams
    } = spaParams;
    const url = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}/salesservices/productProperties`;
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'GET',
        dataType: 'json',
        data: params,
        error: reject,
        success: response => {
          // If PCM Override enabled, merge data from override file
          if (siteParams.pcmOverrideEnabled) {
            const overrideData = overrideProductPropertiesData[params.locale][pageParams.pageID];
            resolve({
              ...response,
              ...overrideData
            });
          }
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Request to check if a zipCode is in a market expansion
   * @param {Object} params - object containing the 'zip' key and value.
   * example zip: 15991
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  requestMarketWithZip(params) {
    const {
      siteParams
    } = spaParams;
    const url = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}/salesservices/validateMarketExpansionZip`;
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'GET',
        dataType: 'json',
        data: params,
        error: reject,
        success: response => {
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Service request to shield
   * @param {string} shieldKey - shield content key
   * @returns {Promise} Promise
   */
  requestShield(shieldKey) {
    const {
      siteParams
    } = spaParams;
    const url = `${siteParams.shieldHost}${siteParams.shieldPath}${shieldKey}`;
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'GET',
        dataType: 'json',
        error: reject,
        success: response => {
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Prefetch product and apply pages of card element in list/category view.
   * In list/category view, only welcome.go page and `Learn More` can be prefetched
   * initialize.go sets cookies and can't be prefetched on pages with more
   * than one `Apply Now` link
   * @param {Object} cardEl Card DOM element.
   * @returns {void}
   */
  prefetchCard(cardEl) {
    if (!cardEl || spaParams.siteParams.disablePerformanceMeta) return;
    const isAlreadyPrefetched = cardEl.classList.contains('spa-prefetch');
    if (isAlreadyPrefetched) return;
    const learnLinkEl = cardEl.querySelector('a.learn');
    const prefetchUrl = href => performanceMetaUtility.addMetaFromObject({
      link: [{
        rel: 'prefetch',
        href
      }]
    });
    const learnLinkHref = learnLinkEl && learnLinkEl.getAttribute('href');
    if (learnLinkHref) prefetchUrl(learnLinkHref);
  }

  /**
     * Delegate multiple event listeners to the module container so
     * dom re-rendering will not double bind etc.
     *
     * @param {String} events - comma separated events to listen for
     * @param {String} selector - selector to delegate to target
     * @param {Function} handler - handler for event being listened for
     * @param {Boolean} once - if true, calls event handler only once
     * @param {HTMLElement} context - HTML Element context
     * @returns {void}
     */
  delegateEvents(events, selector, handler, once, context) {
    const eventsArray = events.split(',').map(item => item.trim());
    if (eventsArray.length > 1) {
      eventsArray.forEach(eventName => {
        this.delegateEvent(eventName, selector, handler, once, context);
      });
    } else {
      this.delegateEvent(events, selector, handler, once, context);
    }
  }

  /**
   * Delegate event listeners to the module container so dom re-rendering will not double bind etc.
   *
   * @param {String} event - event to listen for
   * @param {String} selector - selector to delegate to target
   * @param {Function} handler - handler for event being listened for
   * @param {Boolean} once - if true, calls event handler only once
   * @param {HTMLElement} context - HTML Element context
   * @returns {void}
   */
  delegateEvent(event, selector, handler, once, context) {
    context.addEventListener(event, function delegateTarget(e) {
      for (let {
        target
      } = e; target && target !== this; target = target.parentNode) {
        if (target && target.matches(selector)) {
          if (once) {
            if (e.currentTarget.dataset.triggered) return;
            e.currentTarget.dataset.triggered = true;
          }
          handler.call(target, e);
          break;
        }
      }
    }, false);
  }

  /**
   * replaceTokens on html content.
   *
   * @description Due to legacy cards original ${TOKEN} replacement stuff we need to swap tokens on
   * runtime due to JCRs containing all these tokens. Don't be using eval for security.
   * @param {String} content content
   * @param {Object} data data
   * @returns {String} trimmed value
   */
  replaceTokens(content, data) {
    // eslint-disable-line
    return content.replace(/\${[^}]+}/g, swapToken => {
      const token = swapToken.slice(2, -1);
      let value = data[token];
      if (!value || value === 'null') {
        value = '';
      }
      return value.trim();
    });
  }

  /**
   * getPropertiesFromTemplate
   *
   * @description Returns all the saleservice product map properties based on the template.
   * Migration from legacy which performed this when it sent payload to the service.
   * @param {String} content content data
   * @return {Array} array from parsed tokens
   */
  getPropertiesFromTemplate(content) {
    // eslint-disable-line
    const parsedTokens = new Set(content.match(/\${([^}]+)}/g));

    // Remove the ${ID} since it might raise error from service
    // This was updated in the JCR to not have to process another string replace for the @key
    parsedTokens.delete('${ID}'); // eslint-disable-line

    return Array.from(parsedTokens).map(token => token.slice(2, -1));
  }

  /**
   * @param {object} campaignProperties campaign info
   * @return {void}
   */
  parseFootnotes(campaignProperties) {
    // scan the entire page for footnotes populated from PCM.
    const footnotes = $('[data-footnote-jrn]');
    if (footnotes.length) {
      // eslint-disable-next-line func-names
      footnotes.each(function () {
        this.innerHTML = '<span class="spa-footnote-order"></span>';
        const $this = $(this);
        const footnoteRef = $this.data('footnote-jrn');
        const footnoteContent = Handlebars.Utils.escapeExpression(campaignProperties[footnoteRef]);
        let footnoteLink = `<a href="#ftn_${footnoteRef}_content" id="ftn_${footnoteRef}" data-footnote-content="${footnoteContent}" data-footnote="${footnoteRef}">`;
        footnoteLink += $this.removeAttr('data-footnote-jrn').html();
        footnoteLink += '</a>';
        $this.html(footnoteLink);
      });
      spartaFootnoteUtility.rebuildFootnotes();
    }
  }

  /**
   * Generates content for template partial
   * @param {Object} content  - JCR content
   * @param {Object} data - response from PCM containing properties to match to content
   * @returns {void}
   */
  generateLinkContent(content, data) {
    Handlebars.registerHelper('htmlEscaper', htmlEscaper);
    return JSON.parse(Handlebars.compile(JSON.stringify(content))(data).replace(/\n/g, '\\n'));
  }

  /**
   * Build campaign string from campaign object
   * @param {object} campaignObj campaign
   * @return {void}
   */
  buildCampaignString(campaignObj) {
    return `${campaignObj.campaignID}~${campaignObj.poCode}~${campaignObj.locale}`;
  }

  /**
  * handleTermsLink
  * @param {Event} e - event being handled
  * @returns {void}
  */
  handleTermsLink(e) {
    e.preventDefault();
    spartaUILayersUtility.displayPopup(e.target.href, 'termsAndConditions', 'left=50,top=50,width=1100,height=656,scrollbars');
  }

  /**
  * addClassListToSVG
  * @returns {void}
  */
  static addClassListToSVG() {
    if (!('classList' in document.createElementNS('http://www.w3.org/2000/svg', 'svg'))) {
      const descr = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'classList');
      Object.defineProperty(SVGElement.prototype, 'classList', descr);
    }
  }
}

/**
 * @const creditCardUtility
 * @description single instance of CreditCardUtility used to expose this utility
 */
const creditCardUtility = spartaSingletonUtility('sparta.utility.creditCardUtility', CreditCardUtility);

export { creditCardUtility as default };
